// 44-job ultimate Skill objects. ADR 0011.
//
// Each Skill follows ADR 0007 §2.x concept + ADR 0008 §2.x types + ADR 0009/
// 0010 sim machinery. Damage / heal `power` values are sampled from
// `ultimatePowerBand(strengthClass, { t2Special }).mid` per ADR 0011 §2.2 —
// numerical balance is calibrated in Phase 4.
//
// targeting / range / radius are mapped from concept text per ADR 0011 §2.3:
//   "single / 1-target"        → singleTarget, range 8 (melee) / 25 (ranged)
//   "frontal cone"             → cone length=8, maxTargets=5, range=8
//   "piercing line"            → line length=25, maxTargets=10, range=25
//   "wide AoE / surrounding"   → circle radius=8, maxTargets=8, range=18
//   "self / self-buff"         → self, range=0
//   "ally single / lowest-hp"  → allyLowestHp, range=25
//   "ally party-wide"          → allAllies, range=25
//   "placeable @ caster pos"   → self
//   "placeable @ enemy feet"   → singleTarget + spec.placementTarget='enemy-feet'
import { ULTIMATE_CONCEPT_BY_JOB_ID, } from './ultimates.js';
import { ultimatePowerBand } from './ultimate-power.js';
/** T2 special 5 jobs (ADR 0007 §2.1 + ADR 0011 §2.2). Their `weak-sub` band
 *  uses the upper "T2.5" sub-band rather than the default. */
export const T2_SPECIAL_JOB_IDS = new Set([
    'samurai',
    'summoner',
    'beast-tamer',
    'phantom-thief',
    'ascetic-monk',
]);
function bandMid(strengthClass, jobId) {
    return ultimatePowerBand(strengthClass, { t2Special: T2_SPECIAL_JOB_IDS.has(jobId) }).mid;
}
function conceptOf(jobId) {
    const c = ULTIMATE_CONCEPT_BY_JOB_ID.get(jobId);
    if (c === undefined)
        throw new Error(`No ultimate concept for jobId="${jobId}"`);
    return c;
}
/** Helper: compose a Skill from a job concept + targeting + effects. */
function buildSkill(jobId, options) {
    const c = conceptOf(jobId);
    const skill = {
        id: c.skillId,
        name: c.name,
        source: 'ultimate',
        kind: 'active',
        cooldownTicks: c.cooldownTicks,
        activation: {},
        effects: options.effects,
    };
    if (options.range !== undefined)
        skill.range = options.range;
    if (options.targeting !== undefined)
        skill.targeting = options.targeting;
    if (options.hpCost !== undefined)
        skill.hpCost = options.hpCost;
    return skill;
}
const ALL_ELEMENTS = [
    'physical',
    'fire',
    'ice',
    'lightning',
    'earth',
    'light',
    'dark',
];
// ===========================================================================
// T1 (6 jobs, weak, mid = 1.05)
// ===========================================================================
const T1_SKILLS = [
    buildSkill('warrior', {
        range: 8,
        targeting: { shape: 'cone', length: 8, maxTargets: 5 },
        effects: [{ kind: 'damage', element: 'physical', power: bandMid('weak', 'warrior') }],
    }),
    buildSkill('fortune-teller', {
        range: 25,
        targeting: { shape: 'singleTarget' },
        effects: [
            {
                kind: 'damage',
                element: 'weapon-derived',
                power: bandMid('weak', 'fortune-teller'),
                elementBranch: {
                    fire: [{ kind: 'applyDebuff', debuffId: 'burn', stacks: 1 }],
                    ice: [{ kind: 'applyDebuff', debuffId: 'chill', stacks: 1 }],
                    lightning: [{ kind: 'applyDebuff', debuffId: 'shock', stacks: 1 }],
                    earth: [{ kind: 'applyDebuff', debuffId: 'pressure', stacks: 1 }],
                    light: [{ kind: 'applyDebuff', debuffId: 'dazzle', stacks: 1 }],
                    dark: [{ kind: 'applyDebuff', debuffId: 'curse', stacks: 1 }],
                },
            },
        ],
    }),
    buildSkill('priest-novice', {
        range: 25,
        targeting: { shape: 'allyLowestHp' },
        effects: [
            { kind: 'heal', power: bandMid('weak', 'priest-novice') },
            { kind: 'applyBuff', buffId: 'divine_grace', durationTicks: 60 },
        ],
    }),
    buildSkill('thief', {
        range: 8,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'dash', distance: 5 },
            { kind: 'damage', element: 'physical', power: bandMid('weak', 'thief') },
        ],
    }),
    buildSkill('entertainer', {
        range: 25,
        targeting: { shape: 'allAllies' },
        effects: [{ kind: 'applyBuff', buffId: 'stance', durationTicks: 90 }],
    }),
    buildSkill('hunter', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'hunter.snare',
                    radius: 2,
                    durationTicks: 600,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'contact', consumesOnTrigger: true },
                    onTriggerEffects: [
                        { kind: 'damage', element: 'physical', power: bandMid('weak', 'hunter') },
                        { kind: 'applyDebuff', debuffId: 'crush', stacks: 1 },
                    ],
                },
            },
        ],
    }),
];
// ===========================================================================
// T2 default (6 jobs, weak-sub, mid = 1.075)
// ===========================================================================
const T2_DEFAULT_SKILLS = [
    buildSkill('knight', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            { kind: 'applyBuff', buffId: 'harden', durationTicks: 120 },
            { kind: 'applyDebuff', debuffId: 'crush', stacks: 1, durationTicks: 60 },
        ],
    }),
    buildSkill('mage', {
        range: 25,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'damage', element: 'weapon-derived', power: bandMid('weak-sub', 'mage') },
            { kind: 'damage', element: 'weapon-derived', power: 0.5 },
        ],
    }),
    buildSkill('cleric', {
        range: 25,
        targeting: { shape: 'allyLowestHp' },
        effects: [
            { kind: 'heal', power: bandMid('weak-sub', 'cleric') },
            { kind: 'cleanse', count: 1 },
        ],
    }),
    buildSkill('assassin', {
        range: 8,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'dash', distance: 5 },
            { kind: 'damage', element: 'physical', power: bandMid('weak-sub', 'assassin') },
            { kind: 'applyDebuff', debuffId: 'bleed', stacks: 1 },
        ],
    }),
    buildSkill('bowmaster', {
        range: 25,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'damage', element: 'physical', power: bandMid('weak-sub', 'bowmaster') },
            { kind: 'applyBuff', buffId: 'stance', durationTicks: 90 },
        ],
    }),
    buildSkill('bard', {
        range: 25,
        targeting: { shape: 'allAllies' },
        effects: [
            { kind: 'applyBuff', buffId: 'mighty', durationTicks: 120 },
            { kind: 'applyBuff', buffId: 'divine_grace', durationTicks: 120 },
        ],
    }),
];
// ===========================================================================
// T2 special (5 jobs, weak-sub + t2Special, mid = 1.175)
// ===========================================================================
const T2_SPECIAL_SKILLS = [
    buildSkill('samurai', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'counterStance',
                durationTicks: 90,
                counterPower: bandMid('weak-sub', 'samurai'),
                counterElement: 'physical',
                counterDebuff: { debuffId: 'stance', stacks: 1, durationTicks: 30 },
            },
        ],
    }),
    buildSkill('summoner', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'sum.spirit',
                    durationTicks: 240,
                    count: 2,
                    randomElement: {
                        candidates: ['fire', 'ice', 'lightning', 'earth'],
                        buffByElement: {
                            fire: 'mighty',
                            ice: 'composure',
                            lightning: 'stance',
                            earth: 'harden',
                        },
                        buffDurationTicks: 240,
                        buffTarget: 'allAllies',
                    },
                },
            },
        ],
    }),
    buildSkill('beast-tamer', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            // Surrounding-AoE: encode as a 1-tick placeable so onTriggerEffects pick
            // up the enemy-only side rule (pickPlaceableTargets, ADR 0009 §2.9).
            // ADR 0011 mapping: 「相棒獣が自身周囲の敵をノックバック」.
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'beast-tamer.warden-howl',
                    radius: 8,
                    durationTicks: 30,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'tick-pulse', periodTicks: 30 },
                    onTriggerEffects: [
                        { kind: 'damage', element: 'physical', power: bandMid('weak-sub', 'beast-tamer') },
                        { kind: 'knockback', distance: 3 },
                        { kind: 'applyDebuff', debuffId: 'bleed', stacks: 1 },
                    ],
                },
            },
            { kind: 'applyBuff', buffId: 'stance', durationTicks: 90 },
        ],
    }),
    buildSkill('phantom-thief', {
        range: 25,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'applyDebuff', debuffId: 'armor_break', stacks: 1, durationTicks: 120 },
            { kind: 'applyBuff', buffId: 'mighty', durationTicks: 120 },
            { kind: 'applyBuff', buffId: 'shadow_veil', durationTicks: 120 },
        ],
    }),
    buildSkill('ascetic-monk', {
        range: 25,
        targeting: { shape: 'allAllies' },
        hpCost: 50,
        effects: [
            { kind: 'cleanse', count: 99, delayTicks: 30 },
            { kind: 'heal', power: bandMid('weak-sub', 'ascetic-monk'), delayTicks: 30 },
        ],
    }),
];
// ===========================================================================
// T3 — Section A: Escalator endpoints (6 jobs, mid = 1.40)
// ===========================================================================
const T3_SECTION_A = [
    buildSkill('paladin', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'paladin.aegis',
                    radius: 8,
                    durationTicks: 300,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'aura-zone' },
                    onTriggerEffects: [
                        { kind: 'applyBuff', buffId: 'harden', durationTicks: 60 },
                        { kind: 'damage', element: 'physical', power: bandMid('mid', 'paladin') },
                    ],
                },
            },
        ],
    }),
    buildSkill('archmage', {
        range: 18,
        targeting: { shape: 'circle', radius: 8, maxTargets: 8 },
        effects: [
            {
                kind: 'damage',
                element: 'weapon-derived',
                power: bandMid('mid', 'archmage'),
                elementBranch: {
                    fire: [
                        { kind: 'applyDebuff', debuffId: 'burn', stacks: 1 },
                        {
                            kind: 'placePlaceable',
                            spec: {
                                templateId: 'archmage.fireball',
                                radius: 4,
                                durationTicks: 120,
                                maxConcurrentPerOwner: 1,
                                isWalkable: true,
                                trigger: { kind: 'tick-pulse', periodTicks: 30 },
                                onTriggerEffects: [{ kind: 'damage', element: 'fire', power: 0.5 }],
                            },
                        },
                    ],
                    ice: [{ kind: 'applyDebuff', debuffId: 'freeze', stacks: 1 }],
                    lightning: [{ kind: 'applyDebuff', debuffId: 'paralyze', stacks: 1 }],
                    earth: [{ kind: 'applyDebuff', debuffId: 'brittle', stacks: 1 }],
                    light: [{ kind: 'cleanse', count: 1 }],
                    dark: [{ kind: 'applyDebuff', debuffId: 'curse', stacks: 1 }],
                },
            },
        ],
    }),
    buildSkill('high-priest', {
        range: 25,
        targeting: { shape: 'allAllies' },
        effects: [
            { kind: 'heal', power: bandMid('mid', 'high-priest') },
            { kind: 'applyBuff', buffId: 'divine_grace', durationTicks: 120 },
            { kind: 'cleanse', count: 1 },
        ],
    }),
    buildSkill('ninja', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            { kind: 'applyBuff', buffId: 'shadow_veil', durationTicks: 120 },
            {
                kind: 'summon',
                spec: { unitId: 'ninja.clone', durationTicks: 120, count: 4 },
            },
            // Surrounding-AoE: 1-tick placeable for shuriken throw, enemy-only via
            // pickPlaceableTargets (ADR 0009 §2.9). ADR 0011 mapping: 「分身が手裏剣で対象範囲」.
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'ninja.shuriken-burst',
                    radius: 8,
                    durationTicks: 30,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'tick-pulse', periodTicks: 30 },
                    onTriggerEffects: [
                        { kind: 'damage', element: 'physical', power: bandMid('mid', 'ninja') },
                    ],
                },
            },
        ],
    }),
    buildSkill('bow-saint', {
        range: 25,
        targeting: { shape: 'line', length: 25, maxTargets: 10 },
        effects: [
            { kind: 'damage', element: 'physical', power: bandMid('mid', 'bow-saint') },
            { kind: 'applyDebuff', debuffId: 'crush', stacks: 1, durationTicks: 90 },
        ],
    }),
    buildSkill('gakusei', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'phaseCyclingAura',
                durationTicks: 600,
                phaseTicks: 100,
                phases: ['fire', 'earth', 'lightning', 'light', 'dark', 'ice'],
                buffByElement: {
                    fire: 'mighty',
                    earth: 'harden',
                    lightning: 'stance',
                    light: 'divine_grace',
                    dark: 'blood_oath',
                    ice: 'composure',
                },
            },
        ],
    }),
];
// ===========================================================================
// T3 — Section B: Cross default+default (9 jobs, mid = 1.40)
// ===========================================================================
const T3_SECTION_B = [
    buildSkill('magus-knight', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            { kind: 'applyBuff', buffId: 'harden', durationTicks: 120 },
            // Surrounding pull + AoE damage: 1-tick placeable, enemy-only via
            // pickPlaceableTargets. ADR 0011 mapping: 「周囲の離れた敵を引き寄せ」.
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'magus-knight.runic-vortex',
                    radius: 8,
                    durationTicks: 30,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'tick-pulse', periodTicks: 30 },
                    onTriggerEffects: [
                        { kind: 'pull', distance: 3 },
                        { kind: 'damage', element: 'weapon-derived', power: bandMid('mid', 'magus-knight') },
                    ],
                },
            },
        ],
    }),
    buildSkill('valkyrie', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            { kind: 'applyBuff', buffId: 'harden', durationTicks: 300 },
            {
                kind: 'damageConversion',
                fraction: 0.3,
                durationTicks: 300,
                payoutShape: 'allAllies',
            },
        ],
    }),
    buildSkill('sage', {
        range: 18,
        targeting: { shape: 'circle', radius: 8, maxTargets: 8 },
        effects: [
            { kind: 'damage', element: 'light', power: bandMid('mid', 'sage') },
            { kind: 'heal', power: 1.0 },
            { kind: 'cleanse', count: 1 },
        ],
    }),
    buildSkill('inquisitor', {
        range: 25,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'applyDebuff', debuffId: 'petrify', stacks: 1, durationTicks: 300 },
            {
                kind: 'anchoredHealOnHit',
                durationTicks: 300,
                healPowerPerHit: 0.5,
                payoutShape: 'attacker',
            },
        ],
    }),
    buildSkill('magic-archer', {
        range: 25,
        targeting: { shape: 'circle', radius: 4, maxTargets: 5 },
        effects: [
            {
                kind: 'damage',
                element: 'weapon-derived',
                power: bandMid('mid', 'magic-archer'),
                elementBranch: {
                    fire: [{ kind: 'applyDebuff', debuffId: 'burn', stacks: 1 }],
                    ice: [{ kind: 'applyDebuff', debuffId: 'chill', stacks: 1 }],
                    lightning: [{ kind: 'applyDebuff', debuffId: 'paralyze', stacks: 1 }],
                    earth: [{ kind: 'applyDebuff', debuffId: 'brittle', stacks: 1 }],
                    light: [{ kind: 'applyDebuff', debuffId: 'dazzle', stacks: 1 }],
                    dark: [{ kind: 'applyDebuff', debuffId: 'curse', stacks: 1 }],
                },
            },
        ],
    }),
    buildSkill('shadow-hunter', {
        range: 18,
        targeting: { shape: 'singleTarget' },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'shadow.elemental-snare',
                    radius: 3,
                    durationTicks: 300,
                    maxConcurrentPerOwner: 3,
                    isWalkable: true,
                    placementTarget: 'enemy-feet',
                    trigger: { kind: 'contact', consumesOnTrigger: true },
                    onTriggerEffects: [
                        { kind: 'damage', element: 'physical', power: bandMid('mid', 'shadow-hunter') },
                    ],
                },
            },
        ],
    }),
    buildSkill('enchanter', {
        range: 25,
        targeting: { shape: 'allyLowestHp' },
        effects: [
            {
                kind: 'applyBuff',
                buffId: 'mighty',
                durationTicks: 600,
                onCritEffects: [{ kind: 'damage', element: 'weapon-derived', power: 1.0 }],
            },
        ],
    }),
    buildSkill('astrologer', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'reflectingProjectile',
                projectileCount: 7,
                maxBounces: 5,
                onBounceBuff: { buffId: 'divine_grace', durationTicks: 60 },
            },
        ],
    }),
    buildSkill('divine-archer', {
        range: 25,
        targeting: { shape: 'singleTarget' },
        effects: [
            {
                kind: 'transformOnHit',
                onHit: { element: 'light', power: bandMid('mid', 'divine-archer') },
                afterTransform: [
                    { kind: 'heal', power: 0.5 },
                    { kind: 'applyBuff', buffId: 'harden', durationTicks: 60 },
                ],
            },
        ],
    }),
];
// ===========================================================================
// T3 — Section C: Cross default+special (6 jobs, mid = 1.40)
// ===========================================================================
const T3_SECTION_C = [
    buildSkill('spellblade', {
        range: 8,
        targeting: { shape: 'singleTarget' },
        effects: [
            { kind: 'damage', element: 'weapon-derived', power: bandMid('mid', 'spellblade') },
            { kind: 'knockback', distance: 3 },
            { kind: 'applyDebuff', debuffId: 'crush', stacks: 1, durationTicks: 60 },
        ],
    }),
    buildSkill('dragon-knight', {
        range: 25,
        targeting: { shape: 'line', length: 25, maxTargets: 10 },
        effects: [
            {
                kind: 'damage',
                element: 'weapon-derived',
                power: bandMid('mid', 'dragon-knight'),
                elementBranch: {
                    fire: [{ kind: 'applyDebuff', debuffId: 'burn', stacks: 1 }],
                    ice: [{ kind: 'applyDebuff', debuffId: 'chill', stacks: 1 }],
                    lightning: [{ kind: 'applyDebuff', debuffId: 'paralyze', stacks: 1 }],
                    earth: [{ kind: 'applyDebuff', debuffId: 'brittle', stacks: 1 }],
                    light: [{ kind: 'applyDebuff', debuffId: 'dazzle', stacks: 1 }],
                    dark: [{ kind: 'applyDebuff', debuffId: 'curse', stacks: 1 }],
                },
            },
            { kind: 'applyBuff', buffId: 'harden', durationTicks: 120 },
        ],
    }),
    buildSkill('necromancer', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'necro.skeleton',
                    durationTicks: 120,
                    count: 5,
                    onDeathEffects: [
                        { kind: 'applyDebuff', debuffId: 'curse', stacks: 1, durationTicks: 60 },
                    ],
                },
            },
        ],
    }),
    buildSkill('druid', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'summon',
                spec: { unitId: 'druid.tree-spirit', durationTicks: 600, count: 1 },
            },
        ],
    }),
    buildSkill('trickster', {
        range: 18,
        targeting: { shape: 'circle', radius: 8, maxTargets: 8 },
        effects: [
            { kind: 'applyDebuff', debuffId: 'curse', stacks: 1, durationTicks: 90 },
            { kind: 'applyBuff', buffId: 'mighty', durationTicks: 90 },
        ],
    }),
    buildSkill('puppet-master', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'puppet.melee',
                    durationTicks: 300,
                    count: 2,
                    threadLineAura: { buffId: 'stance', auraRadius: 1.5, buffDurationTicks: 60 },
                },
            },
        ],
    }),
];
// ===========================================================================
// T3 — Section D: Cross special+special (1 job, mid = 1.40)
// ===========================================================================
const T3_SECTION_D = [
    buildSkill('arch-summoner', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'summon',
                spec: {
                    unitId: 'arch.elemental',
                    durationTicks: 300,
                    count: 2,
                    maxConcurrentPerOwner: 16,
                    randomElement: {
                        candidates: ALL_ELEMENTS,
                        buffByElement: {
                            physical: 'mighty',
                            fire: 'heat_aura',
                            ice: 'ice_film',
                            lightning: 'charge',
                            earth: 'earth_guard',
                            light: 'divine_grace',
                            dark: 'blood_oath',
                        },
                        buffDurationTicks: 300,
                        buffTarget: 'caster',
                    },
                },
            },
        ],
    }),
];
// ===========================================================================
// T4 (5 jobs, strong, mid = 1.80)
// ===========================================================================
const T4_SKILLS = [
    buildSkill('sword-saint', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'damageStoreAndRelease',
                storeDurationTicks: 150,
                incomingDamageReduction: 0.6,
                releaseMultiplier: 1.0,
                releaseShape: 'circle',
                releaseRadius: 8,
                releaseElement: 'physical',
            },
            { kind: 'applyBuff', buffId: 'stance', durationTicks: 150 },
            { kind: 'applyDebuff', debuffId: 'crush', stacks: 1, durationTicks: 60 },
        ],
    }),
    buildSkill('grand-sage', {
        range: 18,
        targeting: { shape: 'circle', radius: 8, maxTargets: 8 },
        effects: [
            { kind: 'damage', element: 'physical', power: bandMid('strong', 'grand-sage') },
            { kind: 'damage', element: 'fire', power: bandMid('strong', 'grand-sage') },
            { kind: 'damage', element: 'ice', power: bandMid('strong', 'grand-sage') },
            { kind: 'damage', element: 'lightning', power: bandMid('strong', 'grand-sage') },
            { kind: 'damage', element: 'earth', power: bandMid('strong', 'grand-sage') },
            { kind: 'damage', element: 'light', power: bandMid('strong', 'grand-sage') },
            { kind: 'damage', element: 'dark', power: bandMid('strong', 'grand-sage') },
            { kind: 'heal', power: bandMid('strong', 'grand-sage') },
            { kind: 'applyBuff', buffId: 'mighty', durationTicks: 120 },
            { kind: 'applyDebuff', debuffId: 'armor_break', stacks: 1, durationTicks: 120 },
        ],
    }),
    buildSkill('astral-archer', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'summon',
                spec: { unitId: 'astral.spirit', durationTicks: 300, count: 1 },
            },
            // Wide-AoE meteor strike: 1-tick placeable, enemy-only via
            // pickPlaceableTargets. ADR 0011 mapping: 「広範囲に流星雨で多段大ダメージ」.
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'astral.starfall',
                    radius: 12,
                    durationTicks: 30,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'tick-pulse', periodTicks: 30 },
                    onTriggerEffects: [
                        { kind: 'damage', element: 'physical', power: bandMid('strong', 'astral-archer') },
                    ],
                },
            },
            { kind: 'applyBuff', buffId: 'stance', durationTicks: 120 },
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'astral.stardust',
                    radius: 3,
                    durationTicks: 180,
                    maxConcurrentPerOwner: 3,
                    isWalkable: true,
                    placementTarget: 'enemy-feet',
                    trigger: { kind: 'tick-pulse', periodTicks: 30 },
                    onTriggerEffects: [{ kind: 'damage', element: 'physical', power: 0.5 }],
                },
            },
        ],
    }),
    buildSkill('dark-lord', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'dark-lord.throne',
                    radius: 8,
                    durationTicks: 600,
                    maxConcurrentPerOwner: 1,
                    isWalkable: false,
                    trigger: { kind: 'tick-pulse', periodTicks: 60 },
                    onTriggerEffects: [
                        { kind: 'pull', distance: 2 },
                        { kind: 'applyDebuff', debuffId: 'curse', stacks: 1, durationTicks: 90 },
                        {
                            kind: 'summon',
                            spec: { unitId: 'dark-lord.minion', durationTicks: 120, count: 1 },
                        },
                    ],
                },
            },
            { kind: 'applyBuff', buffId: 'harden', durationTicks: 600 },
        ],
    }),
    buildSkill('saint', {
        range: 0,
        targeting: { shape: 'self' },
        effects: [
            {
                kind: 'placePlaceable',
                spec: {
                    templateId: 'saint.aegis-zone',
                    radius: 10,
                    durationTicks: 300,
                    maxConcurrentPerOwner: 1,
                    isWalkable: true,
                    trigger: { kind: 'aura-zone' },
                    onTriggerEffects: [
                        { kind: 'applyBuff', buffId: 'divine_grace', durationTicks: 60 },
                        { kind: 'applyDebuff', debuffId: 'crush', stacks: 1, durationTicks: 60 },
                    ],
                },
            },
            {
                kind: 'sharedAbsorbCharge',
                durationTicks: 300,
                totalAbsorbRatio: 1.5,
                overflowToHealRatio: 0.5,
                areaRadius: 10,
            },
        ],
    }),
];
/** Canonical 44-entry list. Order: T1 → T2 default → T2 special → T3 (A/B/C/D) → T4. */
export const ULTIMATE_SKILLS = [
    ...T1_SKILLS,
    ...T2_DEFAULT_SKILLS,
    ...T2_SPECIAL_SKILLS,
    ...T3_SECTION_A,
    ...T3_SECTION_B,
    ...T3_SECTION_C,
    ...T3_SECTION_D,
    ...T4_SKILLS,
];
export const ULTIMATE_SKILL_BY_ID = new Map(ULTIMATE_SKILLS.map((s) => [s.id, s]));
//# sourceMappingURL=ultimate-skills.js.map